'use client'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'
import React from 'react'
import type { DateRange } from 'react-day-picker'

export type DatePickerProps = {
  className?: string
  date?: Date
  onSelect?: (date: Date | undefined) => void
  disabled?: boolean | ((date: Date) => boolean)
}

const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  ({ className, date, onSelect, disabled }, ref) => {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            className={cn(
              'w-[240px] justify-start text-left font-normal',
              !date && 'text-muted-foreground',
              className,
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, 'PPP') : <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={onSelect}
            disabled={disabled}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    )
  },
)
DatePicker.displayName = 'DatePicker'

export type DateRangePickerProps = {
  className?: string
  range?: DateRange
  onSelect?: (range: DateRange | undefined) => void
  disabled?: boolean | ((date: Date) => boolean)
}

const DateRangePicker = React.forwardRef<HTMLButtonElement, DateRangePickerProps>(
  ({ className, range, onSelect, disabled }, ref) => {
    const label = range?.from
      ? range.to
        ? `${format(range.from, 'yyyy-MM-dd')} – ${format(range.to, 'yyyy-MM-dd')}`
        : format(range.from, 'yyyy-MM-dd')
      : null

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            className={cn(
              'w-[260px] justify-start text-left font-normal',
              !range?.from && 'text-muted-foreground',
              className,
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {label ?? <span>选择日期</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="z-[60] w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={range}
            onSelect={onSelect}
            numberOfMonths={2}
            disabled={disabled}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    )
  },
)
DateRangePicker.displayName = 'DateRangePicker'

export { DatePicker, DateRangePicker }
